import { H3, P } from "@/components/Typography";
import { useTranslation } from "react-i18next";
import { Alert } from "@/components/Alert";
import { faExclamationTriangle } from "@fortawesome/pro-duotone-svg-icons";
import {
  useConsentFormFields,
  usePostPrivacyEmail,
  usePostPrivacyPolicyUrl,
} from "@/requests/hooks/collect.ts";
import PrivacyPolicyUrlForm from "@/components/Form/PrivacyPolicyUrlForm.tsx";
import PrivacyEmailForm from "@/components/Form/PrivacyEmailForm.tsx";
import Shimmer from "@/components/Shimmer";
import { useEffect } from "react";
import { useTracking } from "@/requests/hooks/app.ts";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { OnboardingProgression } from "@/components/OnboardingProgression";

const PrivacyDetails = () => {
  const { t } = useTranslation();
  const { mutate: sendTracking } = useTracking();
  const {
    data: consentFormFields,
    isPending: isConsentFormFieldsPending,
    isFetching: isConsentFormFieldsFetching,
    refetch: refetchConsentFormFields,
  } = useConsentFormFields();
  const postPrivacyPolicyUrlHook = usePostPrivacyPolicyUrl();
  const postPrivacyEmailHook = usePostPrivacyEmail();
  const {
    onboardingStatus: { isOnboarding },
  } = useOnboardingStatus();

  const { isPending: isPostPrivacyPolicyUrlPending } = postPrivacyPolicyUrlHook;
  const { isPending: isPostPrivacyEmailPending } = postPrivacyEmailHook;

  useEffect(() => {
    sendTracking("control-panel.collect.widget-settings.privacy-details.view");
  }, []);

  useEffect(() => {
    // Trigger to re-render so buttons are disabled while busy.
  }, [isPostPrivacyPolicyUrlPending, isPostPrivacyEmailPending]);

  const renderLoading = () => (
    <>
      <div className="my-10">
        <Shimmer height="35px" width="200px" />
      </div>
      <div className="my-10">
        <Shimmer height="45px" width="700px" />
      </div>
      <div className="my-10">
        <Shimmer height="65px" width="100%" />
      </div>
      <div className="my-30">
        <Shimmer height="70px" width="260px" />
      </div>
    </>
  );

  const renderAlertContent = () => (
    <div>
      <div className="mb-10">
        <H3 className="font-bold">
          {t("settings.widgetSettings.privacyDetails.alert.title")}
        </H3>
      </div>
      <P>{t("settings.widgetSettings.privacyDetails.alert.description")}</P>
    </div>
  );

  const renderAlert = () => (
    <div className="mb-30">
      <Alert
        variant="info"
        icon={faExclamationTriangle}
        iconClassName="h-30 w-30"
        titleSlot={renderAlertContent()}
        rounded="unset"
      />
    </div>
  );

  const renderPrivacyPolicySection = () => {
    if (isConsentFormFieldsPending) return renderLoading();

    return (
      <PrivacyPolicyUrlForm
        privacyPolicyUrl={consentFormFields?.privacyPolicyUrl}
        version={consentFormFields?.version}
        isDataFetching={isConsentFormFieldsFetching}
        isPostPrivacyEmailPending={isPostPrivacyEmailPending}
        postPrivacyPolicyUrlHook={postPrivacyPolicyUrlHook}
        refetchConsentFormFields={refetchConsentFormFields}
      />
    );
  };

  const renderPrivacyEmailSection = () => {
    if (isConsentFormFieldsPending) return renderLoading();

    return (
      <PrivacyEmailForm
        privacyEmail={consentFormFields?.privacyEmail}
        version={consentFormFields?.version}
        isDataFetching={isConsentFormFieldsFetching}
        isPostPrivacyPolicyUrlPending={isPostPrivacyPolicyUrlPending}
        postPrivacyEmailHook={postPrivacyEmailHook}
        refetchConsentFormFields={refetchConsentFormFields}
      />
    );
  };

  const renderOnboardingProgression = () =>
    !!isOnboarding && (
      <div className="my-50">
        <OnboardingProgression
          currentStep="CONFIGURE_PRIVACY_DETAILS"
          enabled={
            !!consentFormFields?.privacyEmail &&
            !!consentFormFields?.privacyPolicyUrl
          }
        />
      </div>
    );

  return (
    <>
      {renderAlert()}
      {renderPrivacyPolicySection()}
      {renderPrivacyEmailSection()}
      {renderOnboardingProgression()}
    </>
  );
};

export default PrivacyDetails;
